import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap, finalize, filter, take, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '~backoffice/app/~shared/services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { AUTH_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import { OtpData, FormViolation } from '~core/models/shared.models';
import { getErrorMessage } from '~backoffice/app/~shared/utils/utils';
import { ROUTES } from '~backoffice/app/~shared/constants/routes.constants';

@Component({
  selector: 'reset-password-form',
  standalone: false,
  templateUrl: './reset-password-form.component.html',
  styleUrls: ['./reset-password-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordFormComponent
  extends BaseAuthComponent
  implements OnInit
{
  private _router = inject(Router);
  private _toastService = inject(ToastService);

  routes = ROUTES;

  isGettingResetOtp$ = this._authFacade.isGettingResetOtp$;
  isFormValid$ = this._authFacade.isResetRequestFormValid$;
  resetOtpData$ = this._authFacade.resetOtpData$ as Observable<
    OtpData | null | undefined
  >;

  violations$ = (
    this._globalFacade.formViolations$ as Observable<
      FormViolation[] | undefined
    >
  ).pipe(
    filter(violations => !!violations),
    tap(violations => {
      violations.forEach(violation => {
        if (violation.propertyPath === 'email' && this.email) {
          this.email.setErrors({ httpError: violation.title });
        }
      });
      this._cdRef.detectChanges();
    })
  );

  forgotPasswordForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });

  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.reset.requestFormValues`;

  ngOnInit(): void {
    this.resetOtpData$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(data => !!data),
        take(1),
        tap((otpData: OtpData | null | undefined) => {
          if (!otpData) {
            this._toastService.open({
              content:
                'Aucune session de réinitialisation trouvée. Veuillez recommencer.',
              type: 'error'
            });
            this._router.navigate([ROUTES.resetPassword], {
              replaceUrl: true
            });
            return;
          }

          const emailToUse = otpData.email ?? this.email?.value;
          if (!emailToUse) {
            this._toastService.open({
              content:
                'Aucune adresse email trouvée. Veuillez recommencer le processus.',
              type: 'error'
            });
            this._router.navigate([ROUTES.resetPassword], {
              replaceUrl: true
            });
            return;
          }
          this.email?.setValue(emailToUse);
          this.email?.disable({ emitEvent: false });
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  onSubmit(): void {
    const emailValue = this.email?.value?.trim();
    if (!emailValue) {
      this._toastService.open({
        content: 'Veuillez entrer une adresse e-mail.',
        type: 'error'
      });
      return;
    }

    this.forgotPasswordForm.disable();

    (
      this._authFacade.getResetOneTimePassword() as Observable<
        OtpData | undefined
      >
    )
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(responseData => {
          const emailToUse = responseData?.email ?? emailValue;

          if (!emailToUse) {
            this._toastService.open({
              content: 'Aucune donnée email reçue du serveur.',
              type: 'error'
            });
            this._cdRef.detectChanges();
            return;
          }

          this._toastService.open({
            content:
              'Un code de vérification a été envoyé à votre adresse e-mail',
            type: 'success'
          });

          this._router.navigate([ROUTES.forgotPassword], {
            replaceUrl: true
          });
        }),
        tap({
          error: (error: HttpErrorResponse) => {
            if (error.status === 404) {
              this.email?.setErrors({
                httpError: "Cette adresse e-mail n'est pas associée à un compte"
              });
              this._toastService.open({
                content: "Cette adresse e-mail n'est pas associée à un compte",
                type: 'error'
              });
            } else {
              this._toastService.open({
                content: getErrorMessage(error),
                type: 'error'
              });
            }
            this._cdRef.detectChanges();
          }
        }),
        finalize(() => this.forgotPasswordForm.enable())
      )
      .subscribe();
  }

  get email(): AbstractControl | null {
    return this.forgotPasswordForm.get('email');
  }
}
